import * as api from '@apis/admin-review-events';
import { ReviewEventModal } from '@components/review-event/ReviewEventModal';
import { useAuthentication } from '@hooks/authentication';
import { ReviewEvent } from '@models/review-event';
import { useReviewEventsStore } from '@stores/reviewEventsStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Input, Table } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ReviewEventPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const searchInputRef = useRef<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ReviewEvent | undefined>();
  const store = useReviewEventsStore();
  const dialog = useOkCancelDialog();
  const navigate = useNavigate();

  useAuthentication();

  const getReviewEventsQuery = useQuery({
    queryKey: [`review-events`],
    queryFn: () => api.getReviewEvents(),
  });

  const deleteReviewEventMutation = useMutation({
    mutationFn: (reviewEventId: string) => api.deleteReviewEvent(reviewEventId),
    onSuccess: () => {
      toast.dark('리뷰 이벤트가 삭제되었습니다', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
      getReviewEventsQuery.refetch();
    },
  });

  const handleDelete = (record: ReviewEvent) => {
    dialog.open({
      title: '리뷰 이벤트 삭제',
      content: `"${record.title}" 리뷰 이벤트를 삭제하시겠습니까?`,
      confirmText: '삭제',
      confirmColor: '#ff4d4f',
      type: 'ok_cancel',
      onConfirm: () => {
        deleteReviewEventMutation.mutate(record._id);
      },
    });
  };

  const reviewEvents = getReviewEventsQuery.data?.data;

  useEffect(() => {
    if (reviewEvents) {
      store.setReviewEventsContainer(reviewEvents);
    }
  }, [reviewEvents]);

  const TABLE_COLUMNS: ColumnsType<ReviewEvent> = [
    {
      title: '상세보기',
      align: 'center',
      width: 100,
      render: (record: ReviewEvent) => {
        return <Button onClick={() => navigate(`/review-events/${record._id}`)}>상세보기</Button>;
      },
    },
    {
      title: '후보 리뷰 수',
      align: 'center',
      width: 100,
      render: (record: ReviewEvent) => {
        return record.candidatesCount;
      },
    },
    {
      title: '선정 리뷰 수',
      align: 'center',
      width: 100,
      render: (record: ReviewEvent) => {
        return record.selectedReviewIds?.length || 0;
      },
    },
    {
      title: '제목',
      dataIndex: 'title',
      render: (title) => {
        return title;
      },
    },
    {
      title: '시작일',
      dataIndex: 'startDate',
      render: (startDate) => {
        return moment(startDate).format('YYYY.MM.DD');
      },
    },
    {
      title: '종료일',
      dataIndex: 'endDate',
      render: (endDate) => {
        return moment(endDate).format('YYYY.MM.DD');
      },
    },
    {
      title: '발표일',
      dataIndex: 'announcementDate',
      render: (announcementDate) => {
        return moment(announcementDate).format('YYYY.MM.DD');
      },
    },
    {
      title: '발표일시',
      dataIndex: 'announcementSentAt',
      render: (announcementSentAt) => {
        return announcementSentAt ? moment(announcementSentAt).format('YYYY.MM.DD HH:mm') : '-';
      },
    },
    {
      title: '생성일시',
      dataIndex: 'createdAt',
      render: (createdAt) => {
        return moment(createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      title: '수정일시',
      dataIndex: 'updatedAt',
      render: (updatedAt) => {
        return moment(updatedAt).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      title: '관리',
      render: (record: ReviewEvent) => {
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              onClick={() => {
                setSelectedEvent(record);
                setIsModalOpen(true);
              }}>
              수정
            </Button>
            <Button danger onClick={() => handleDelete(record)}>
              삭제
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <div className="page-header">
        <div>리뷰 이벤트 관리</div>
        <div>
          <Button
            onClick={() => {
              setSelectedEvent(undefined);
              setIsModalOpen(true);
            }}>
            리뷰 이벤트 생성
          </Button>
        </div>
      </div>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              width: '100%',
              height: '52px',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Input
                className={filterActiveClassName(!!store.searchQuery.keyword)}
                ref={searchInputRef}
                style={{ width: '240px' }}
                value={searchKeyword}
                placeholder={'검색어 입력 후 엔터'}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                }}
                onKeyUp={(e) => {
                  if (e.code === 'Enter' && searchKeyword) {
                    store.updateSearchQuery({ keyword: searchKeyword });
                  }
                }}></Input>
              <Button
                onClick={() => {
                  setSearchKeyword(undefined);
                  store.resetSearchQuery();
                }}>
                검색 초기화
              </Button>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((review) => review?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={getReviewEventsQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>

      <ReviewEventModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEvent(undefined);
        }}
        onSuccess={() => {
          getReviewEventsQuery.refetch();
        }}
        initialData={selectedEvent}
        mode={selectedEvent ? 'edit' : 'create'}
      />
    </div>
  );
};

export default ReviewEventPage;
